import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { FaPhoneAlt, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import styles from './Tarjetas.module.css';
import portada from '../../Assets/1.svg';
import dorso from '../../Assets/2.svg';

const sanitizeFilename = (str) =>
  (str || 'tarjeta')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'tarjeta';

const buildQrValue = ({ qrType, phone, message, customUrl, vcardData }) => {
  if (qrType === 'whatsapp') {
    const clean = (phone || '').replace(/[^0-9]/g, '');
    if (!clean) return '';
    return message
      ? `https://wa.me/${clean}?text=${encodeURIComponent(message)}`
      : `https://wa.me/${clean}`;
  }
  if (qrType === 'url') return (customUrl || '').trim();
  if (qrType === 'vcard') {
    const { name, role, phone: p, email, address } = vcardData;
    const cleanPhone = (p || '').replace(/[^0-9+]/g, '');
    return [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `FN:${name || ''}`,
      `TITLE:${role || ''}`,
      `ORG:BY Raíces`,
      cleanPhone ? `TEL;TYPE=CELL:${cleanPhone}` : '',
      email ? `EMAIL:${email}` : '',
      address ? `ADR:;;${address};;;;` : '',
      'END:VCARD',
    ]
      .filter(Boolean)
      .join('\n');
  }
  return '';
};

const Tarjetas = () => {
  const navigate = useNavigate();
  const frontRef = useRef(null);
  const backRef = useRef(null);

  const [form, setForm] = useState({
    name: '',
    role: 'ASESORA INMOBILIARIO',
    phone: '',
    email: '',
    address: 'Hipolito Yrigoyen 226,\nGodoy Cruz',
    qrType: 'whatsapp', // 'whatsapp' | 'url' | 'vcard'
    qrMessage: '',
    qrUrl: '',
  });
  const [isGenerating, setIsGenerating] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const qrValue = useMemo(
    () =>
      buildQrValue({
        qrType: form.qrType,
        phone: form.phone,
        message: form.qrMessage,
        customUrl: form.qrUrl,
        vcardData: {
          name: form.name,
          role: form.role,
          phone: form.phone,
          email: form.email,
          address: form.address.replace(/\n/g, ' '),
        },
      }),
    [form],
  );

  const handleDownloadPdf = async () => {
    if (!frontRef.current || !backRef.current) return;
    if (!qrValue) {
      alert('El QR está vacío. Verificá el teléfono o la URL ingresada.');
      return;
    }
    setIsGenerating(true);
    try {
      const opts = {
        scale: 3,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
      };
      const frontCanvas = await html2canvas(frontRef.current, opts);
      const backCanvas = await html2canvas(backRef.current, opts);

      // 90 x 55 mm (tarjeta personal estándar)
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: [90, 55] });
      pdf.addImage(frontCanvas.toDataURL('image/png'), 'PNG', 0, 0, 90, 55, undefined, 'FAST');
      pdf.addPage([90, 55], 'landscape');
      pdf.addImage(backCanvas.toDataURL('image/png'), 'PNG', 0, 0, 90, 55, undefined, 'FAST');
      pdf.save('tarjeta agente by raices.pdf');
    } catch (err) {
      console.error('Error generando PDF:', err);
      alert('Hubo un error generando el PDF. Revisá la consola.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.headerRow}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>← Volver</button>
        <div>
          <h1 className={styles.pageTitle}>Generador de Tarjetas</h1>
          <p className={styles.pageSubtitle}>
            Completá los datos del agente, previsualizá la tarjeta y descargá el PDF listo para imprimir (90×55 mm).
          </p>
        </div>
      </div>

      <div className={styles.contentGrid}>
        {/* ===== Formulario ===== */}
        <div className={styles.formCard}>
          <h2>Datos del agente</h2>

          <div className={styles.field}>
            <label htmlFor="name">Nombre completo</label>
            <input id="name" name="name" value={form.name} onChange={handleChange} placeholder="Agustín Rosa" />
          </div>

          <div className={styles.field}>
            <label htmlFor="role">Rol</label>
            <input id="role" name="role" value={form.role} onChange={handleChange} placeholder="ASESORA INMOBILIARIO" />
          </div>

          <div className={styles.field}>
            <label htmlFor="phone">Teléfono (con código país)</label>
            <input id="phone" name="phone" value={form.phone} onChange={handleChange} placeholder="+542616808087" />
            <span className={styles.hint}>Se usa también para armar el QR de WhatsApp.</span>
          </div>

          <div className={styles.field}>
            <label htmlFor="email">Email</label>
            <input id="email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="arosa@byraices.com" />
          </div>

          <div className={styles.field}>
            <label htmlFor="address">Dirección</label>
            <textarea
              id="address"
              name="address"
              rows={2}
              value={form.address}
              onChange={handleChange}
              placeholder="Hipolito Yrigoyen 226, Godoy Cruz"
            />
            <span className={styles.hint}>Usá saltos de línea para que se vea en dos renglones.</span>
          </div>

          <h2>QR</h2>
          <div className={styles.field}>
            <label htmlFor="qrType">Destino del QR</label>
            <select id="qrType" name="qrType" value={form.qrType} onChange={handleChange}>
              <option value="whatsapp">WhatsApp (usa el teléfono)</option>
              <option value="url">URL personalizada</option>
              <option value="vcard">Contacto vCard (se guarda en agenda)</option>
            </select>
          </div>

          {form.qrType === 'whatsapp' && (
            <div className={styles.field}>
              <label htmlFor="qrMessage">Mensaje predeterminado (opcional)</label>
              <textarea
                id="qrMessage"
                name="qrMessage"
                rows={2}
                value={form.qrMessage}
                onChange={handleChange}
                placeholder="Hola! Vi tu tarjeta de BY Raíces..."
              />
            </div>
          )}

          {form.qrType === 'url' && (
            <div className={styles.field}>
              <label htmlFor="qrUrl">URL</label>
              <input id="qrUrl" name="qrUrl" value={form.qrUrl} onChange={handleChange} placeholder="https://..." />
            </div>
          )}

          <div className={styles.actionsRow}>
            <button className={styles.primaryBtn} onClick={handleDownloadPdf} disabled={isGenerating}>
              {isGenerating ? 'Generando PDF...' : '⬇️ Descargar PDF'}
            </button>
          </div>
        </div>

        {/* ===== Previsualización ===== */}
        <div className={styles.previewWrapper}>
          <span className={styles.previewLabel}>Frente</span>
          <div className={styles.cardWrap}>
            <div
              ref={frontRef}
              className={`${styles.card} ${styles.cardFront}`}
              style={{ backgroundImage: `url(${portada})` }}
            />
          </div>

          <span className={styles.previewLabel}>Dorso</span>
          <div className={styles.cardWrap}>
            <div
              ref={backRef}
              className={`${styles.card} ${styles.cardBack}`}
              style={{ backgroundImage: `url(${dorso})` }}
            >
              <h2 className={styles.backName}>{form.name || 'NOMBRE APELLIDO'}</h2>
              <p className={styles.backRole}>{form.role || 'ROL'}</p>

              <div className={styles.backBottom}>
                <div className={styles.qrBox}>
                  {qrValue ? (
                    <QRCodeCanvas value={qrValue} size={188} level="H" includeMargin={false} />
                  ) : (
                    <span style={{ color: '#aaa', fontSize: 12, textAlign: 'center' }}>
                      Completá el QR para previsualizarlo
                    </span>
                  )}
                </div>

                <div className={styles.contactList}>
                  {form.phone && (
                    <div className={styles.contactRow}>
                      <span className={styles.contactIcon}><FaPhoneAlt /></span>
                      <span className={styles.contactText}>{form.phone}</span>
                    </div>
                  )}
                  {form.email && (
                    <div className={styles.contactRow}>
                      <span className={styles.contactIcon}><FaEnvelope /></span>
                      <span className={styles.contactText}>{form.email}</span>
                    </div>
                  )}
                  {form.address && (
                    <div className={styles.contactRow}>
                      <span className={styles.contactIcon}><FaMapMarkerAlt /></span>
                      <span className={styles.contactText}>{form.address}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tarjetas;
